import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { StripedDataGrid } from "components/StripedDataGrid";
import { GridColDef } from "@mui/x-data-grid";
import BackdropLoading from "components/BackdropLoading";
import { useNavigation } from "contexts/notificationsContext";
import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";

interface ModalAdicionarN2Props {
  open: boolean;
  onClose: () => void;
  inquilinoId: string;
  inquilinoNome: string;
  onSuccess: () => void;
}

interface ITipologiaDisponivel {
  id: string;
  projetista: string;
  linha: string;
  data: string;
}

/**
 * Modal para adicionar liberações de Cadastro N2 (Tipologias) para CEM MOBI
 * 
 * IMPORTANTE: CEM MOBI só trabalha com conteúdos "Disponível Mercado"
 * Não há abas de Validação ou Homologação
 */
export const ModalAdicionarN2: React.FC<ModalAdicionarN2Props> = ({
  open,
  onClose,
  inquilinoId,
  inquilinoNome,
  onSuccess,
}) => {
  const { openNotification } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [tipologiasDisponiveis, setTipologiasDisponiveis] = useState<ITipologiaDisponivel[]>([]);
  const [selecionados, setSelecionados] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      carregarTipologiasDisponiveis();
    }
  }, [open]);

  /**
   * Carrega tipologias disponíveis (apenas "Disponível Mercado")
   */
  const carregarTipologiasDisponiveis = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get(`/LiberacaoMOBI/tipologiasDisponiveisN2/${inquilinoId}`);
      setTipologiasDisponiveis(data || []);
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Salva liberações selecionadas
   */
  const handleSalvar = async () => {
    if (selecionados.length === 0) {
      openNotification({
        message: "Selecione pelo menos uma tipologia para liberar",
        type: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      await api.post("/LiberacaoMOBI/cadastroN2", {
        inquilinoId,
        tipologiasIds: selecionados,
      });

      openNotification({
        message: `${selecionados.length} tipologia(s) liberada(s) com sucesso!`,
        type: "success",
      });
      
      onSuccess();
      handleClose();
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelecionados([]);
    onClose();
  };

  const handleSelectionChange = (selectionModel: any[]) => {
    setSelecionados(selectionModel.map(String));
  };

  const columns: GridColDef[] = [
    {
      field: "projetista",
      headerName: "Projetista",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "linha",
      headerName: "Linha",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "data",
      headerName: "Data",
      flex: 1,
      minWidth: 120,
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={isLoading}
      >
        <DialogTitle>
          Adicionar materiais para a CEM MOBI - {inquilinoNome}
        </DialogTitle>
        
        <DialogContent>
          {/* Aba MERCADO (única para MOBI) */}
          <Box sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#3C90AD" }}>
              MERCADO
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Apresenta os Projetistas + Linhas/Sistemas que possuem tipologias na situação "Disponível Mercado".
            </Typography>
          </Box>

          {/* Descrição */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Os Projetistas + Linhas/Sistemas selecionados são os que ficarão disponíveis para importação no software do cliente.
          </Typography>

          {/* Tabela de seleção */}
          <Box sx={{ height: 400 }}>
            <StripedDataGrid
              columns={columns}
              rows={tipologiasDisponiveis}
              getRowId={(row) => row.id}
              loading={isLoading}
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selecionados}
              onSelectionModelChange={handleSelectionChange}
              autoHeight={false}
            />
          </Box>

          {/* Contador */}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
            {selecionados.length} item(ns) selecionado(s)
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            CANCELAR
          </Button>
          <Button
            variant="contained"
            onClick={handleSalvar}
            disabled={isLoading || selecionados.length === 0}
          >
            SALVAR
          </Button>
        </DialogActions>
      </Dialog>

      <BackdropLoading isOpen={isLoading} />
    </>
  );
};

